import { registerTrigger, triggerNamedNotify, subscribeNotify } from '../../@helpers/js/observer.js';
import { filterInt } from '../../@helpers/js/helpers.js';
import { wait } from '../../@helpers/js/wait.js';
import { processQueue } from '../../@helpers/js/queue-processing.js';


class PopupOverlay {
    constructor(el, { overlayIsVisibleClass }) {
        //options
        this.o = {
            overlayIsVisibleClass,
        };

        // selectors
        this.s = {
            content: '[data-popup-overlay-content]',
            source: (value) => `[data-popup-overlay-source${value ? `="${value}"`:''}]`,
            trigger: (value) => `[data-popup-overlay-trigger${value ? `="${value}"`:''}]`,
            inputContainer: '[data-input]',
            dialogtrigger: (value) => `[data-popup-dialog-trigger${value ? `="${value}"`:''}]`,
            dialoginnertrigger: (value) => `[data-popup-dialog-trigger${value ? `="${value}"`:''}] *`,
            nosourcetrigger: (value) => `[data-popup-overlay-trigger${value ? `="${value}"`:''}]:not([data-popup-overlay-nosource])`,
            close: '[data-popup-overlay-close],[data-popup-dialog-close]',
            closeText: '[data-popup-overlay-close-text]',
            okText: '[data-popup-overlay-ok-text]',
            ok: '[data-popup-dialog-ok]',
        };

        // elements
        this.$e = {
            root: el,
            body: document.documentElement,
            page: document.querySelector('.o-just-page') || document.querySelector('.o-page'),
            content: el.querySelector(this.s.content),
            sources: document.querySelectorAll(this.s.source()),
            closers: el.querySelectorAll(this.s.close),
            closeText: el.querySelectorAll(this.s.closeText),
            okText: el.querySelectorAll(this.s.okText),
            ok: el.querySelector(this.s.ok)
        };

        this.c = {
            FOCUSABLE_ELEMENTS: [
                'a[href]:not([tabindex^="-"]):not([inert])',
                'area[href]:not([tabindex^="-"]):not([inert])',
                'input:not([disabled]):not([inert])',
                'select:not([disabled]):not([inert])',
                'textarea:not([disabled]):not([inert])',
                'button:not([disabled]):not([inert])',
                'iframe:not([tabindex^="-"]):not([inert])',
                'audio:not([tabindex^="-"]):not([inert])',
                'video:not([tabindex^="-"]):not([inert])',
                '[contenteditable]:not([tabindex^="-"]):not([inert])',
                '[tabindex]:not([tabindex^="-"]):not([inert])'
            ],
            TAB_KEY: 9,
            ESCAPE_KEY: 27
        };

        this.evHandlers = {
            keepFocus: (e) => this.keepFocus(e),
            keyPress: (e) => this.onKeypress(e),
            backbuttonPress: (e) => this.onBackbutton(e),
            closeClickPress: (e) => this.handleCloseClick(e)
        }

        // init
        this.init();
    }

    init() {
        // Hide all overlay content
        this.$e.root.setAttribute('inert', true);
        this.$e.root.classList.add('t-hidden');
        this.$e.sources.forEach($source => {
            $source.classList.add('t-hidden');
        });

        registerTrigger('PopupOverlay_closeOverlay');
        registerTrigger('PopupDialog_okOverlay');
        this.popupQueue = processQueue(({target, within = false}) => this.handleTriggerClick(target, within),1000);

        const addToProcessQueue = ({target, within}) => {
            if (!target || !(target.matches(this.s.trigger()) || target.matches(this.s.dialogtrigger()) || target.matches(this.s.dialoginnertrigger())) || target.disabled) {
                return;
            }
            let $trigger = target;
            if ($trigger.matches(this.s.dialoginnertrigger())) {
                $trigger = target.closest(this.s.dialogtrigger());
            }
            const sourceId = $trigger?.dataset?.popupOverlayTrigger || $trigger?.dataset?.popupDialogTrigger;
            if (sourceId && !this.$e.root.querySelector(this.s.source(sourceId))) { // if not already showing
                this.popupQueue.push({target, within});
            }
        }

        document.body.addEventListener('click', (e) => {
            if (!e.target || e.target.matches(this.s.inputContainer)) {
                return;
            }
            addToProcessQueue(e);
        });

        subscribeNotify('showPopup_triggered', ({ trigger, within }) => {
            if (trigger && (trigger.matches(this.s.inputContainer) || trigger.matches(this.s.trigger()))) {
                addToProcessQueue({target: trigger, within});
                return true;
            }
        });

        this.$e.closers.forEach((close) => close.addEventListener('click', this.evHandlers.closeClickPress));
        this.$e.root.addEventListener('click', (e) => this.handleClickOutside(e));
        if (this.$e.ok) {
            this.$e.ok.addEventListener('click', (e) => {
                const $closeTrigger = this.$e.closers[0];
                const originId = $closeTrigger.dataset.popupOverlayTriggerSource;
                this.handleCloseClick(e);
                triggerNamedNotify('PopupDialog_okOverlay', {
                    name: originId
                });
            })
        }
    }

    getFocusableChildren() {
        return [...this.$e.root.querySelectorAll(this.c.FOCUSABLE_ELEMENTS.join(','))].filter((child) => !!(
            child.offsetWidth ||
            child.offsetHeight ||
            child.getClientRects().length
        ));
    }

    setFocusToFirstItem() {
        const focusableChildren = this.getFocusableChildren();
        const focused = this.$e.root.querySelector('[autofocus]') || focusableChildren[0];

        if (focused) {
            focused.focus();
        }
        return !!focused;
    }

    handleTriggerClick(target, within = false) {
        let $trigger = target;
        if (!$trigger || !($trigger.matches(this.s.trigger()) || $trigger.matches(this.s.dialogtrigger()) || $trigger.matches(this.s.dialoginnertrigger())) || $trigger.disabled) {
            return;
        }
        const $closeTrigger = this.$e.closers[0];
        const oldId = $closeTrigger.dataset.popupOverlayTriggerSource;

        if (this.$e.root.classList.contains('c-popup-dialog')){
            this.clearOverlay(oldId);
        }
        
        if ($trigger.matches(this.s.dialoginnertrigger())) {
            $trigger = target.closest(this.s.dialogtrigger());
        }
        if ($trigger.matches(this.s.dialogtrigger()) || $trigger.matches(this.s.dialoginnertrigger())) {
            this.clearOverlay(oldId);
            this.$e.root.classList.add('c-popup-dialog');
        }

        const sourceId = $trigger.dataset.popupOverlayTrigger || $trigger.dataset.popupDialogTrigger;

        let $source = [...this.$e.sources].filter($source => {
            if ($source.dataset.popupOverlaySource === sourceId) {
                return $source;
            }
        })[0];

        if (!$source && within) {
            $source = $trigger.querySelector(this.s.source(sourceId))
        }

        if (!$source) {
            $source = document.body.querySelector(this.s.source(sourceId))
        }

        if ($source) {
            const closeText = $source.dataset.closeText;
            const okText = $source.dataset.okText;
            const autoHidePopup = $source.matches('[data-autohide-popup]') && $source.dataset.autohidePopup.trim() && $source.dataset.autohidePopup.trim().toLowerCase() !== 'false';
            const hideDelay = autoHidePopup ? filterInt($source.dataset.autohidePopup.trim()) || 7000 : 7000;
            if (closeText && this.$e.closeText && this.$e.closeText.length > 0) {
                this.$e.closeText.forEach(ct => ct.innerHTML = closeText);
            }
            if (okText && this.$e.okText && this.$e.okText.length > 0) {
                this.$e.okText.forEach(ot => ot.innerHTML = okText);
            }
            this.$e.closers[0].setAttribute('data-popup-overlay-trigger-source', sourceId);
            if (within) {
                this.$e.closers[0].setAttribute('data-popup-within', within);
            }
            const closeMe = $source.querySelector(this.s.close);
            if (closeMe) {
                closeMe.setAttribute('data-popup-overlay-trigger-source', sourceId);
                closeMe.addEventListener('click', this.evHandlers.closeClickPress);
                this.$e.closers[this.$e.closers.length - 1].classList.add('t-hidden');
            }

            this.$e.content.insertAdjacentElement('afterbegin', $source);
            
            $source.classList.remove('t-hidden');
            this.showOverlay(autoHidePopup);
            if (autoHidePopup === true) {
                setTimeout(() => {
                    this.handleCloseClick({autoHide: true, target: closeMe});
                }, hideDelay);
            }
        }
    }

    handleClickOutside(e) {
        if (e.target === e.currentTarget) {
            this.handleCloseClick(e);
        }
    }

    handleCloseClick(e) {
        const $closeTrigger = (e && e.target && e.target.dataset?.popupOverlayTriggerSource) ? e.target : this.$e.closers[0];
        const originId = $closeTrigger.dataset.popupOverlayTriggerSource;

        this.clearOverlay(originId);
        if (!this.$e.content.querySelector('[data-popup-overlay-source]')) {
            this.$e.closers[this.$e.closers.length - 1].classList.remove('t-hidden');
            this.hideOverlay().then(() => {
                this.$e.root.classList.remove('c-popup-dialog');
                this.$e.root.removeAttribute('data-popup-state');
                const $triggerSource = document.body.querySelector(this.s.nosourcetrigger(originId)) || document.body.querySelector(this.s.dialogtrigger(originId));
                triggerNamedNotify('PopupOverlay_closeOverlay', {
                    name: originId
                });
                triggerNamedNotify('PopupDialog_closeOverlay', {
                    name: originId
                });
                if (e && e.preventDefault) {
                    e.preventDefault();
                }
                if ($triggerSource) {
                    if (!e || !e.autoHide){
                        $triggerSource.focus();
                    }
                }
            });
        }
    }

    clearOverlay(originId) {
        if (!originId) {
            return;
        }
        let $triggerSource = document.body.querySelector(this.s.nosourcetrigger(originId)) || document.body.querySelector(this.s.dialogtrigger(originId));
        let $source = [...this.$e.sources].filter($source => {
            if ($source.dataset.popupOverlaySource === originId) {
                return $source;
            }
        })[0];
        if (!$source) {
            $source = document.body.querySelector(this.s.source(originId));
        }
        if ($source) {
            const within = this.$e.closers[0].getAttribute('data-popup-within');
            this.$e.closers[0].removeAttribute('data-popup-within');
            this.$e.closers[0].removeAttribute('data-popup-overlay-trigger-source');
            $source.classList.add('t-hidden');
            const closeMe = $source.querySelector(this.s.close);
            if (closeMe) {
                closeMe.removeAttribute('data-popup-overlay-trigger-source');
                closeMe.removeEventListener('click', this.evHandlers.closeClickPress);
            }
            if ($triggerSource) {
                if ($triggerSource.matches(`input[type="radio"]`)) {
                    $triggerSource = $triggerSource.nextElementSibling;
                }
                $triggerSource.insertAdjacentElement(within ? 'beforeend' : 'afterend', $source);
            } else {
                document.body.insertAdjacentElement('beforeend', $source);
            }
        }
    }

    isOpen() {
        return this.$e.root.classList.contains(this.o.overlayIsVisibleClass);
    }

    async showOverlay(autoHide) {
        this.$e.root.removeAttribute('inert');
        this.$e.root.classList.remove('t-hidden');
        await wait(0);
        this.$e.root.classList.add(this.o.overlayIsVisibleClass);
        
        if (!autoHide) {
            this.$e.page.setAttribute('inert', true);
            // Set the focus to the first focusable child of the dialog element
            if (this.setFocusToFirstItem()) {
                document.body.addEventListener('focus', this.evHandlers.keepFocus, true);
            }
            document.addEventListener('keydown', this.evHandlers.keyPress);
            if (window.history.state === 'showOverlay') {
                window.removeEventListener('popstate', this.evHandlers.backbuttonPress);
            } else {
                if (window.history.state === 'backPressed') {
                    window.removeEventListener('popstate', this.evHandlers.backbuttonPress);
                } else {
                    this._oldState = window.history.state;
                    window.history.replaceState('backPressed', null, null);
                }
                window.history.pushState('showOverlay', null, null);
            }
            window.addEventListener('popstate', this.evHandlers.backbuttonPress);
        }
    }

    onBackbutton(event) {
        if (event && event.state == 'backPressed' && this.isOpen()) {
            this.handleCloseClick(event);
        }
    }

    onKeypress(event) {
        // If the dialog is shown and the ESCAPE key is being pressed, prevent any
        // further effects from the ESCAPE key and hide the dialog, unless its role
        // is 'alertdialog', which should be modal
        if (event.which === this.c.ESCAPE_KEY && (!event.target.matches('input[type="search"]') || !event.target.value)) {  
            let topNotification = this.$e.content.querySelector('[data-popup-overlay-trigger-source]');
            if (topNotification) {
                this.handleCloseClick({target: topNotification});
            } else {
                this.handleCloseClick(event);
            }
        }

        // If the dialog is shown and the TAB key is being pressed, make sure the
        // focus stays trapped within the dialog element
        if (event.which === this.c.TAB_KEY) {
            this.trapTabKey(event);
        }
    }

    trapTabKey(event) {
        const focusableChildren = this.getFocusableChildren();
        const focusedItemIndex = focusableChildren.indexOf(document.activeElement);

        if (focusableChildren && focusableChildren.length > 0) {

            // If the SHIFT key is being pressed while tabbing (moving backwards) and
            // the currently focused item is the first one, move the focus to the last
            // focusable item from the dialog element
            if (event.shiftKey && focusedItemIndex === 0) {
                focusableChildren[focusableChildren.length - 1].focus();
                event.preventDefault();
                // If the SHIFT key is not being pressed (moving forwards) and the currently
                // focused item is the last one, move the focus to the first focusable item
                // from the dialog element
            } else if (
                !event.shiftKey &&
                focusedItemIndex === focusableChildren.length - 1
            ) {
                focusableChildren[0].focus();
                event.preventDefault();
            }
        }
    }

    keepFocus(event) {
        // If the dialog is shown and the focus is not within the dialog element,
        // move it back to its first focusable child
        if (!this.$e.root.contains(event.target)) {
            this.setFocusToFirstItem();
        }
    }

    async hideOverlay() {
        this.$e.page.removeAttribute('inert');
        this.$e.root.classList.remove(this.o.overlayIsVisibleClass);
        await wait(400);
        this.$e.root.setAttribute('inert', true);
        this.$e.root.classList.add('t-hidden');

        // Remove the focus event listener to the body element and stop listening
        // for specific key presses
        document.body.removeEventListener('focus', this.evHandlers.keepFocus, true);
        document.removeEventListener('keydown', this.evHandlers.keyPress);
        window.removeEventListener('popstate', this.evHandlers.backbuttonPress);
        if (window.history.state === 'backPressed' && !!this._oldState && this._oldState !== 'backPressed') {
            window.history.replaceState(this._oldState, null, null);
        }
    }
}

export default PopupOverlay;